import { UploadManager } from './upload_manager.js';

import ServerTime from '../../../lib/server_time.js';

import { StoreController } from '../../../lib/lit/store_controller.js';

import {
  setupPusherGlobalAckSystem,
  pusherAckTrigger,
  pusherConfig,
} from './pusher.js';
import { onSet, computed } from 'nanostores';
import ChatModel from './chat.model.js';
import { store, computedChatsStore } from './chats.store.js';
import { getNewMessages } from './compute_state_for_current_chat.js';

import {
  csrfToken,
  extractImageSize,
  nextFrame,
} from '../../../lib/helpers.js';

ServerTime.sync();

import { VerifyPatch } from './verify_patch.js';

import { ChatMessageEvent } from '../../chat_message_base/lib/chat_message_event.js';
import UnsentMessagesManager from './unsent_messages_manager.js';
import './crypto_subtle_helper.js';
import './local_store_helper.js';

import debounce from 'lodash-es/debounce.js';
import produce from 'immer';

setupPusherGlobalAckSystem();

export class ChatController extends StoreController {
  uploaders = UploadManager.uploaders;

  unsentMessagesManager = new UnsentMessagesManager({
    translateFn: (...args) => this.t(...args),
    uploaders: this.uploaders,
    localStoreNamespace: `chat-${this.chatId}`,
  });

  debouncedMarkAllAsRead = debounce(this.markAllAsRead, 300);

  constructor(host) {
    super(host);
    this.pusher = pusherConfig.pusher;
  }

  update(attributes) {
    return this.chatModel.updateMessage(attributes);
  }

  hostConnected() {
    this.setupAtom();
    this.setupEvents();
    this.setupUnsentMessagesManager();
    this.subscribeToPusherChannel();
    this.initialLoad();
    this.activateChat();
    this.loadSignature();
    this.periodicallyRefreshSignature();
    super.hostConnected();
  }

  activateChat() {
    ChatModel.setActive(this.chatId);
  }

  deactivateChat() {
    ChatModel.setActive(null);
  }

  initializeChatModel() {
    this.chatModel.initialize({
      kind: this.kind,
      membersCount: this.membersCount,
      chatUrl: this.chatUrl,
      channelName: this.channelName,
    });

    this.chatModel.storedChat.loaded || this.chatModel.load();
  }

  async loadSignature() {
    const signature = await this.chatModel.fetchCurrentUserSignature();
    console.count('CHAT loadSignature');
    this.host.signature = signature;
  }

  periodicallyRefreshSignature() {
    this.periodicallyRefreshSignatureInterval = setInterval(
      // TODO: Try to remove async once all the tests are written
      async () => {
        if (!document.hidden) this.loadSignature();
      },
      60 * 1000 // 60 seconds
    );
  }

  async verifySignature(payload) {
    const verificator = new VerifyPatch({
      publicKey: this.publicKey,
      chatId: this.chatId,
      store: store.value,
    });

    return verificator.verifySignature(payload);
  }

  hostDisconnected() {
    clearInterval(this.periodicallyRefreshSignatureInterval);
    this.channel?.unbind();
    this.channel?.unsubscribe();

    this.removeEvents();
    this.deactivateChat();

    super.hostDisconnected();
    this.keepLastXMessages({ limit: 40 });
  }

  keepLastXMessages({ limit }) {
    if (!limit) throw new Error('limit is required');
    if (!this.value?.sorted.length) return;

    const { sorted } = this.value;
    const toRemove = sorted.slice(0, -limit);
    toRemove.forEach(m => {
      this.chatModel.removeMessage(m.uuid);
    });
  }

  get chatModel() {
    return (this._chat ||= new ChatModel(this.chatId));
  }

  get currentUser() {
    return this.host.currentUser;
  }

  get kind() {
    return this.host.kind;
  }

  get membersCount() {
    return this.host.membersCount;
  }

  get perPage() {
    return this.host.perPage;
  }

  get chatUrl() {
    return this.host.chatUrl;
  }

  get chatId() {
    return this.host.chatId;
  }

  get channelName() {
    return this.host.channelName;
  }

  get publicKey() {
    return this.host.publicKey;
  }

  get lastMessageReceivedAwayFromBottomAt() {
    return this.host.lastMessageReceivedAwayFromBottomAt;
  }

  t(...args) {
    return this.host.t(...args);
  }

  toggleMute() {
    return this.chatModel.toggleMute();
  }

  markChatAsRead() {
    return this.chatModel.markChatAsRead();
  }

  setupAtom() {
    this.atom = computed(computedChatsStore, ({ chats }) => {
      const chat = chats.get(this.chatId);

      if (!chat) return;

      return produce(chat, draft => {
        draft.newMessages = getNewMessages(
          draft.sorted,
          this.lastMessageReceivedAwayFromBottomAt
        );
      });
    });
  }

  setupEvents() {
    onSet(this.atom, this.onSet.bind(this));
    document.addEventListener('pusher-retry-exhausted', this);
  }

  removeEvents() {
    document.removeEventListener('pusher-retry-exhausted', this);
  }

  handleEvent(e) {
    e.type === 'pusher-retry-exhausted' && this.processExhaustedRetry(e.detail);
  }

  setupUnsentMessagesManager() {
    this.unsentMessagesManager = new UnsentMessagesManager({
      translateFn: (...args) => this.t(...args),
      uploaders: this.uploaders,
      localStoreNamespace: `chat-${this.chatId}`,
    });
  }

  async initialLoad() {
    this.initializeChatModel();

    this.loadUnsentMessages();

    // Rendering in webkit looks less jumpy if we wait for next frame
    await nextFrame();
    await this.loadMessages();
    this.debouncedMarkAllAsRead();
  }

  shouldUpdateUnsentMessages(newValue) {
    if (!newValue) return;

    const currentValue = this.value;

    if (currentValue?.sorted.length !== newValue.sorted.length) return true;

    return newValue.sorted.some(m => {
      const oldMessage = currentValue?.messages?.get(m.uuid);
      return (
        m.receivedAt !== oldMessage?.receivedAt ||
        m.savedAt !== oldMessage?.savedAt
      );
    });
  }

  onSetUpdateUnsentMessages(newValue) {
    if (!this.shouldUpdateUnsentMessages(newValue)) return;

    const updates = [];

    newValue.messages?.forEach(m => {
      if (m.savedAt || m.receivedAt) {
        updates.push({
          uuid: m.uuid,
          unsent: null,
          errors: null,
        });
      }
    });

    this.chatModel.upsertMessages(updates);
    this.unsentMessagesManager.update(newValue.unsentMessages);
  }

  onSet({ newValue }) {
    const currentValue = this.atom.value;

    if (newValue?.sorted?.length > currentValue?.sorted?.length) {
      // This also works on first load and when we load more messages
      // Add some delay because markMessagesAsReceived is called first and we
      // don't want to send double mark as read requests
      this.debouncedMarkAllAsRead();

      this.host.resetScrollIndicatorVisibility();
    }

    this.onSetUpdateUnsentMessages(newValue);
  }

  async loadMessages({ query, showLoader = true } = {}) {
    await this.chatModel.loadMessages({
      chatUrl: this.chatUrl,
      query,
      showLoader,
      perPage: this.perPage || 30,
    });
  }

  insert(message) {
    return this.chatModel.insertMessage(message);
  }

  remove(uuid) {
    this.chatModel.removeMessage(uuid);
  }

  updateAndBroadcast(attributes) {
    const [patches, nextState] = this.update(attributes);
    patches.length && this.pusherTrigger('client-message:update', { patches });
    return [patches, nextState];
  }

  delete({ uuid, deletedByUserName }) {
    return this.updateAndBroadcast({
      uuid,
      deletedByUserName,
      deletedAt: ServerTime.toJSON(),
    });
  }

  restore(uuid) {
    return this.updateAndBroadcast({ uuid, deletedAt: null });
  }

  edit(params) {
    return this.updateAndBroadcast({
      ...params,
      editedAt: ServerTime.toJSON(),
    });
  }

  retry(uuid) {
    const message = this.chatModel.getMessage(uuid);
    this.chatModel.removeMessage(uuid);

    const [patches] = this.chatModel.insertMessage({
      ...message,
      unsent: null,
      errors: null,
      createdAt: ServerTime.toJSON(),
    });

    patches && this.pusherTrigger('client-message', { patches });
  }

  markAllAsRead() {
    const unreadMessagesIds = this.value?.unreadMessages
      ?.filter(m => !m.errors && m.kind !== 'system')
      ?.map(m => m.uuid);

    if (!unreadMessagesIds?.length) return;

    const [patches] = this.chatModel.markAllAsReadByUser({
      userId: this.currentUser.id,
      unreadMessagesIds,
    });

    patches.length &&
      this.pusherTrigger('client-message:update', {
        method: 'markAllAsRead',
        patches,
      });
  }

  loadUnsentMessages() {
    const { messages } = this.unsentMessagesManager;
    const withoutExisting = messages.filter(({ uuid }) => {
      return !this.value.messages.get(uuid);
    });

    this.chatModel.upsertMessages(withoutExisting);
  }

  processExhaustedRetry({ payload }) {
    payload.patches.forEach(patch => {
      console.log('processExhaustedRetry: ', patch);
      const { op, path } = patch;
      const [ns1, chatId, ns2, uuid] = patch.path;

      // We only care about messages from this chat
      if (chatId !== this.chatId) return;

      if (
        path.length === 4 &&
        op === 'add' &&
        ns1 === 'chats' &&
        ns2 === 'messages'
      ) {
        this.chatModel.updateMessage({
          chatId,
          uuid,
          unsent: true,
          errors: [this.t('errors.not_delivered')],
        });
      }
    });
  }

  // Pusher ***********************************************************

  subscribeToPusherChannel() {
    if (this.subscribed) return;

    this.channel = this.pusher.subscribe(this.channelName);

    this.channel
      // Browser to browser
      .bind('client-message', p => this.onMessageReceived(p, true))
      .bind('client-message:update', p => this.onMessageUpdate(p, true))
      // Server
      .bind('message', p => this.onMessageReceived(p))
      .bind('message:update', p => this.onMessageUpdate(p));

    this.subscribed = true;
  }

  async applyPatches(payload, isClient) {
    const verifiedPatches = isClient
      ? await this.verifySignature(payload)
      : payload.patches;

    ChatModel.applyPatches(verifiedPatches);
  }

  onMessageUpdate(payload, isClient) {
    return this.applyPatches(payload, isClient);
  }

  get missingParentMessagesInReplies() {
    const { sorted, messages } = this.value;

    const replies = sorted.filter(m => m.parent?.uuid);

    return replies.filter(m => !messages.has(m.parent.uuid));
  }

  /**
   * User can receive a reply to an old message which we need to load
   *
   * @internal
   */
  loadParentMessages() {
    const promises = this.missingParentMessagesInReplies.map(m => {
      this.chatModel.loadMessage({
        chatUrl: this.chatUrl,
        messageId: m.parent.uuid,
      });
    });

    return Promise.all(promises);
  }

  async onMessageReceived(payload, isClient) {
    this.host.setupNewMessagesNotification();
    this.host.dispatchEvent(new ChatMessageEvent('received'));
    await this.applyPatches(payload, isClient);

    this.loadParentMessages();

    // We only need confirmation for client messages
    const ack = payload.transaction ? { id: payload.transaction.id } : {};

    const messageIds = payload.patches
      .map(p => p.op === 'add' && p.value.uuid)
      .filter(Boolean);

    this.markMessagesAsReceived(messageIds, ack);
  }

  markMessagesAsReceived(messageIds, ack) {
    for (const uuid of messageIds) {
      // Shared changes
      const [patches] = this.chatModel.updateMessage({
        uuid,
        receivedAt: ServerTime.toJSON(),
        readByUserId: this.currentUser.id,
      });

      const method = 'markMessagesAsReceived';

      this.pusherTrigger('client-message:update', { method, patches, ack });
    }
  }

  pusherTrigger(eventName, data, ...args) {
    data.signature = this.host.signature;
    return pusherAckTrigger(this.channel, eventName, data, ...args);
  }

  // CRUD *********************************

  saveViaUpload(message) {
    const uploadManager = new UploadManager({
      message,
      endpoint: this.chatUrl,
      chatModel: this.chatModel,
      csrfToken: csrfToken(),
    });

    uploadManager.upload();
  }

  async extendMessageWithFileAttributes(message) {
    const { file } = message;

    if (!file) return;

    message.uploading = true;
    message.sentAt = ServerTime.toJSON();

    if (file.type.includes('image')) {
      const imagePreviewUrl = URL.createObjectURL(file);

      message.imagePreviewUrl = imagePreviewUrl;
      message.images = {
        imageSmall: imagePreviewUrl,
        imageMedium: imagePreviewUrl,
        imageLarge: imagePreviewUrl,
      };

      const { width, height } = await extractImageSize(imagePreviewUrl);
      message.imageWidth = width;
      message.imageHeight = height;
    }
  }

  async createMessage({ value: body, files, parent }) {
    const file = files?.[0];
    const message = this.chatModel.newMessage({
      author: this.currentUser,
      isUserOfficial: this.host.currentUser.isRepresentative,
      body,
      file,
      parent: parent ?? {
        uuid: parent?.uuid,
      },
    });

    this.host.dispatchEvent(new ChatMessageEvent('created'));
    await this.extendMessageWithFileAttributes(message);

    const [patches] = this.insert(message);

    if (file) {
      this.saveViaUpload(message);
    } else {
      this.pusherTrigger('client-message', {
        method: 'createMessage',
        patches,
      });

      this.chatModel.updateMessage({
        ...message,
        sentAt: ServerTime.toJSON(),
      });
    }
  }
}
